/**
 * Identity normalization layer (Phase 1 — vendor alignment).
 * ──────────────────────────────────────────────────────────────────
 * Single source of truth for translating raw vendor / org identity
 * payloads into a canonical `{ role, capabilities, sources, ... }`
 * shape consumed by every gating surface (sidebar, settings, teams,
 * scheduler, identity headers).
 *
 * Authority rules — DO NOT WEAKEN IN THIS PHASE:
 *
 *   1.  `userAccessMe.permissions` (server-computed capability map
 *       from /api/user-access/me) is **runtime authority** for
 *       capability gates. When present, it is honoured verbatim.
 *
 *   2.  `userAccessMe.app_role` is the canonical role when present.
 *
 *   3.  Vendor data (`authMe.role`, `teamMember.non_medical_role`,
 *       `teamMember.category === 'medical'`) is used **only** to
 *       derive a role when (1) and (2) are absent or empty.
 *
 *   4.  Unknown / unaliased vendor values fall back to
 *       `'read_only'` (least-privilege) and emit
 *       `vendor_role_normalization_miss` telemetry, which is
 *       deduped per-session.
 *
 * The output also carries a `sources` map so UI / telemetry / debug
 * panels can see *why* a particular role was chosen:
 *
 *   {
 *     from_user_access:           bool,
 *     from_vendor_role:           bool,
 *     from_team_non_medical_role: bool,
 *     from_team_category:         bool,
 *     fallback_read_only:         bool,
 *   }
 */

import {
  resolveRole,
  capabilitiesFor,
  unmappedRoleTelemetry,
  UNKNOWN_ROLE_FALLBACK,
} from './permissions';

/* Vendor role → canonical alias map. Spot-mapped, not exhaustive —
 * see permissions.ROLE_ALIASES for the full set. We re-test here to
 * decide whether a vendor token gave us *something we recognize* so
 * we can flip the right `sources` flag. */
const isAliased = (raw) => {
  if (raw == null) return false;
  const v = String(raw).trim().toLowerCase();
  if (!v) return false;
  // Resolve in silent mode so re-testing doesn't fire telemetry; if the
  // resolver returns the canonical fallback for a non-empty input, the
  // raw value wasn't aliased.
  return resolveRole({ role: v }, { silent: true }) !== UNKNOWN_ROLE_FALLBACK
      || v === 'client'   // 'client' canonically maps to itself
      || v === 'user';
};

/* Empty-shaped identity result — used when caller has nothing yet
 * (e.g. before the first auth/me round-trip completes). */
const EMPTY = Object.freeze({
  role: UNKNOWN_ROLE_FALLBACK,
  capabilities: Object.freeze({
    role: UNKNOWN_ROLE_FALLBACK,
    canTeamAdmin: false,
    canSchedulerAdmin: false,
    canSchedulerWrite: false,
    canSchedulerRead: false,
    canSchedulerSummary: false,
    _source: 'fallback',
  }),
  sources: Object.freeze({
    from_user_access:           false,
    from_vendor_role:           false,
    from_team_non_medical_role: false,
    from_team_category:         false,
    fallback_read_only:         true,
  }),
  displayName: '',
  displayEmail: '',
});

/**
 * Compute the visible name for an identity. Fallback chain (per spec):
 *   display_name → name → first+last → email-local-part → 'User'
 * Never returns 'Front Desk' or any other persona literal.
 */
export function deriveDisplayName(authMe) {
  if (!authMe || typeof authMe !== 'object') return 'User';
  const dn = (authMe.display_name || authMe.displayName || '').toString().trim();
  if (dn) return dn;
  const nm = (authMe.name || authMe.full_name || '').toString().trim();
  if (nm) return nm;
  const fn = (authMe.first_name || authMe.firstName || authMe.given_name || '').toString().trim();
  const ln = (authMe.last_name  || authMe.lastName  || authMe.family_name || '').toString().trim();
  const combo = `${fn} ${ln}`.trim();
  if (combo) return combo;
  const email = (authMe.email || '').toString().trim();
  if (email && email.includes('@')) {
    const local = email.split('@')[0].trim();
    if (local) return local;
  }
  return 'User';
}

/** 2-letter initials derived from `deriveDisplayName`. Never persona-literal. */
export function deriveInitials(authMe) {
  const name = deriveDisplayName(authMe);
  if (name === 'User') return 'U';
  return name
    .split(/[\s._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || 'U';
}

/**
 * Normalize identity into the canonical shape.
 *
 *   normalizeIdentity({ authMe, userAccessMe, teamMember, opts })
 *
 *   authMe         — raw /api/auth/me response (or null)
 *   userAccessMe   — raw /api/user-access/me response (or null)
 *   teamMember     — raw /api/team/members entry for the caller (or null)
 *   opts.silent    — suppress telemetry (used by tests)
 */
export function normalizeIdentity(input = {}) {
  const { authMe, userAccessMe, teamMember, opts } = input;
  const silent = !!(opts && opts.silent);

  if (!authMe && !userAccessMe && !teamMember) return { ...EMPTY };

  const sources = {
    from_user_access:           false,
    from_vendor_role:           false,
    from_team_non_medical_role: false,
    from_team_category:         false,
    fallback_read_only:         false,
  };

  // ── Determine role using the documented priority ladder ─────────
  let role = null;

  // 1 · /api/user-access/me.app_role  (highest)
  if (userAccessMe && userAccessMe.app_role) {
    const r = resolveRole({ app_role: userAccessMe.app_role }, { silent: true });
    role = r;
    sources.from_user_access = true;
  }

  // 2 · /api/auth/me.role  (vendor role)
  if (!role && authMe && authMe.role) {
    if (isAliased(authMe.role)) {
      role = resolveRole({ role: authMe.role }, { silent: true });
      sources.from_vendor_role = true;
    } else {
      // Vendor handed us a string we don't recognize — telemetry +
      // fall through so a downstream signal can still produce a
      // mapped role. This is the new "vendor_role_normalization_miss"
      // bucket.
      if (!silent) {
        unmappedRoleTelemetry(
          String(authMe.role).trim().toLowerCase(),
          'vendor_role_normalization_miss',
        );
      }
    }
  }

  // 3 · team member non_medical_role
  if (!role && teamMember && teamMember.non_medical_role) {
    if (isAliased(teamMember.non_medical_role)) {
      role = resolveRole(
        { non_medical_role: teamMember.non_medical_role },
        { silent: true },
      );
      sources.from_team_non_medical_role = true;
    } else if (!silent) {
      unmappedRoleTelemetry(
        String(teamMember.non_medical_role).trim().toLowerCase(),
        'team_non_medical_role_miss',
      );
    }
  }

  // 4 · team member category
  if (!role && teamMember) {
    const cat = String(teamMember.category || '').trim().toLowerCase();
    if (cat === 'medical' || cat === 'medical_provider' || cat === 'medical-provider') {
      role = 'provider';
      sources.from_team_category = true;
    }
  }

  // 5 · least-privilege fallback
  if (!role) {
    role = UNKNOWN_ROLE_FALLBACK;
    sources.fallback_read_only = true;
  }

  // ── Build a synthetic user object to feed `capabilitiesFor` ──────
  // `permissions` from /api/user-access/me remains *runtime authority*
  // — capabilitiesFor honours it verbatim when present. Otherwise we
  // fall back to the canonical role we just derived.
  const userForCaps = {
    role,
    app_role: role,
    permissions:
      userAccessMe && userAccessMe.permissions && typeof userAccessMe.permissions === 'object'
        ? userAccessMe.permissions
        : null,
  };
  const capabilities = capabilitiesFor(userForCaps);

  return {
    role,
    capabilities,
    sources: Object.freeze(sources),
    displayName: deriveDisplayName(authMe),
    displayEmail: (authMe && authMe.email) ? String(authMe.email) : '',
  };
}

/** Convenience: normalize from the merged user object the AuthContext
 *  builds (which already squashes auth/me + user-access/me together).
 *  Useful for code paths that haven't been updated to thread the raw
 *  payloads. */
export function normalizeIdentityFromUser(user) {
  if (!user) return { ...EMPTY };
  return normalizeIdentity({
    authMe: user,
    userAccessMe: user.app_role || user.permissions
      ? { app_role: user.app_role || null, permissions: user.permissions || null }
      : null,
    teamMember: null,
    opts: { silent: false },
  });
}
